import { useQuery, useMutation, useQueryClient } from 'react-query'
import { config } from 'config'
import { callAxios, CustomAxiosError } from 'utils/api'
import { AVAILABILITY_TARGET_DEFAULT_VALUE } from 'constants/supplierTarget'
import {
  AvailabilityTargetData,
  GetAvailabilityTargetRawResponse,
  GetAvailabilityTargetRequest,
  SetAvailabilityTargetRequest,
  SetAvailabilityTargetResponse,
} from 'types/supplierTarget'

const { SECURE_BASE_URL } = config!

const getAvailabilityTarget = async (props: GetAvailabilityTargetRequest): Promise<AvailabilityTargetData> => {
  try {
    const { response: targetResponse } = await callAxios<{
      response: GetAvailabilityTargetRawResponse
    }>({
      url: `${SECURE_BASE_URL}/getavailabletarget`,
      method: 'GET',
    })

    const mappedResponse = (): AvailabilityTargetData => {
      const targetNumberString = targetResponse?.data
        ?.find((item) => !!item?.availableTarget)
        ?.availableTarget.replace(/\D/g, '')

      const targetNumberValue =
        !!targetNumberString && !isNaN(parseInt(targetNumberString)) ? parseInt(targetNumberString) : null

      return {
        availableTarget: targetNumberValue ?? AVAILABILITY_TARGET_DEFAULT_VALUE,
      }
    }

    return mappedResponse()
  } catch (e) {
    //Fallback in case API fails, still load default target value
    return { availableTarget: AVAILABILITY_TARGET_DEFAULT_VALUE }
  }
}

export const useGetAvailabilityTarget = (props: GetAvailabilityTargetRequest) => {
  const {
    status: getAvailabilityTargetStatus,
    isFetching: getIsAvailabilityTargetLoading,
    data: availabilityTargetData,
    error: getAvailabilityTargetError,
  } = useQuery<AvailabilityTargetData, CustomAxiosError>(['availabilityTarget/get', props], () =>
    getAvailabilityTarget(props)
  )

  return {
    getAvailabilityTargetStatus,
    getIsAvailabilityTargetLoading,
    availabilityTargetData,
    getAvailabilityTargetError,
  }
}

const setAvailabilityTarget = async (props: SetAvailabilityTargetRequest): Promise<SetAvailabilityTargetResponse> => {
  const { response: targetResponse } = await callAxios<{
    response: SetAvailabilityTargetResponse
  }>({
    url: `${SECURE_BASE_URL}/setavailabletarget`,
    method: 'POST',
    data: {
      availableTarget: `${props.availableTarget?.toString() ?? AVAILABILITY_TARGET_DEFAULT_VALUE.toString()}%`,
      updatedBy: props.updatedBy,
    },
  })

  return targetResponse
}

export const useSetAvailabilityTarget = () => {
  const queryClient = useQueryClient()

  const {
    status: getSetAvailabilityTargetStatus,
    data: setAvailabilityTargetData,
    error: getSetAvailabilityTargetError,
    mutate: mutateSetAvailabilityTarget,
    reset: resetSetAvailabilityTarget,
  } = useMutation<SetAvailabilityTargetResponse, CustomAxiosError, SetAvailabilityTargetRequest>(
    setAvailabilityTarget,
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(['availabilityTarget/get'])
      },
    }
  )

  return {
    getSetAvailabilityTargetStatus,
    setAvailabilityTargetData,
    getSetAvailabilityTargetError,
    mutateSetAvailabilityTarget,
    resetSetAvailabilityTarget,
  }
}
